import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/LangContext";

const easeOutExpo = [0.16, 1, 0.3, 1];

function LineReveal({ children, delay = 0 }) {
  return (
    <span className="reveal-mask">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.2, ease: easeOutExpo, delay }}
        className="block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacityImg = useTransform(scrollYProgress, [0, 0.8], [0.55, 0.1]);
  const yBadge = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Parallax image */}
      <motion.div
        aria-hidden
        style={{ y: yImg, scale: scaleImg, opacity: opacityImg }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.unsplash.com/photo-1587049633312-d628ae50a8ae?auto=format&fit=crop&w=2400&q=85"
          alt="Emergent Food artisanal kitchen"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
      </motion.div>

      {/* Faint grid */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Corner meta */}
      <motion.div
        style={{ y: yBadge }}
        className="absolute top-24 lg:top-28 right-6 lg:right-10 z-10 text-right hidden md:block"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-mono-ef">
          Coordinates
        </div>
        <div className="mt-2 font-mono-ef text-xs text-white/80">45.4642° N · 9.1900° E</div>
        <div className="mt-1 font-mono-ef text-xs text-white/40">Milan · EU</div>
      </motion.div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-16 lg:pb-24 min-h-[100svh] flex flex-col justify-between">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_12px_rgba(239, 68, 68,0.8)]" />
            {t.hero.overline}
          </motion.div>

          <h1
            className="mt-8 lg:mt-12 font-display font-black tracking-[-0.045em] leading-[0.88] text-[13vw] sm:text-[11vw] lg:text-[8.4vw] xl:text-[7.6vw]"
            data-testid="hero-title"
          >
            {t.hero.title_lines.map((line, i) => (
              <LineReveal key={line + i} delay={0.5 + i * 0.12}>
                <span className={i === t.hero.title_lines.length - 1 ? "text-red-400" : ""}>
                  {line}
                </span>
              </LineReveal>
            ))}
          </h1>

          <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              className="lg:col-span-6 xl:col-span-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="lg:col-span-6 xl:col-span-7 flex flex-col sm:flex-row lg:justify-end gap-3"
            >
              <Link to="/start" data-testid="hero-cta-primary" className="btn-pill btn-primary">
                {t.hero.cta_primary}
                <svg className="ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <a href="#process" data-testid="hero-cta-secondary" className="btn-pill btn-ghost">
                {t.hero.cta_secondary}
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-16 lg:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10 border-t border-white/10 pt-8 lg:pt-10"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} className="flex items-start gap-4">
              <span className="font-mono-ef text-[10px] text-white/40 mt-2">0{i + 1}</span>
              <div>
                <div className="font-display text-3xl lg:text-5xl font-bold text-white leading-none">
                  {s.k}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.16em] text-white/50">{s.v}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40 font-mono-ef"
      >
        <span>{t.hero.scroll_hint}</span>
        <span className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
}
